import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GalleryService } from '../services/gallery.service';
import { SpinnerService } from 'src/app/all-modules/shared/services/spinner.service';
import { CommonToastService } from 'src/app/all-modules/shared/services/common-toast.service';
import { ApiResponse } from 'src/app/all-modules/shared/common-model/common.model';
import { PublicGalleryViewModel, GalleryPhoto, GalleryComment } from '../model/gallery.model';

@Component({
  selector: 'app-secure-photo-gallery',
  templateUrl: './secure-photo-gallery.component.html',
  styleUrl: './secure-photo-gallery.component.scss',
  standalone: false
})
export class SecurePhotoGalleryComponent implements OnInit, OnDestroy {
  // Body classes are the only way to reach <body> from a component; both are scoped to this page
  // and removed when it is destroyed. See the matching rules in styles.scss.
  private static readonly PAGE_CLASS = 'pg-public-gallery';
  private static readonly LIGHTBOX_CLASS = 'pg-lightbox-open';

  token = '';
  contactRef = '';
  // ?p= — the commented photo the SMS pointed at; carried through to the app deep link so a
  // Leadman lands back on the same photo rather than the grid.
  deepPhotoId = '';
  loading = true;
  expired = false;
  loadError = false;
  gallery!: PublicGalleryViewModel;
  allPhotos: GalleryPhoto[] = [];

  lightboxOpen = false;
  activeIndex = 0;
  comments: GalleryComment[] = [];
  loadingComments = false;
  posting = false;
  private touchStartX = 0;

  constructor(private route: ActivatedRoute, private galleryService: GalleryService,
    private spinner: SpinnerService, private toast: CommonToastService,
    private cdr: ChangeDetectorRef) {
    this.route.params.subscribe(p => this.token = p['token']);
    this.route.queryParams.subscribe(q => {
      this.contactRef = q['c'] || '';
      this.deepPhotoId = q['p'] || '';
    });
  }

  ngOnInit() {
    document.body.classList.add(SecurePhotoGalleryComponent.PAGE_CLASS);
    this.loadGallery();
  }

  ngOnDestroy() {
    document.body.classList.remove(SecurePhotoGalleryComponent.PAGE_CLASS);
    document.body.classList.remove(SecurePhotoGalleryComponent.LIGHTBOX_CLASS);
  }

  loadGallery() {
    this.loading = true;
    this.spinner.show();
    this.galleryService.getPhotoGallery(this.token, this.contactRef).subscribe({
      next: (res: PublicGalleryViewModel) => {
        this.spinner.hide();
        this.loading = false;
        if (!res || res.status !== 'active') {
          this.expired = true;
          this.cdr.detectChanges();
          return;
        }
        this.gallery = res;
        this.allPhotos = [];
        for (const day of (res.days || [])) {
          for (const photo of (day.photos || [])) {
            this.allPhotos.push(photo);
          }
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.spinner.hide();
        this.loading = false;
        this.loadError = true;
        console.error('[PhotoGallery] failed to load gallery', err);
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * A way back into the MMISafe app for our own people.
   *
   * On Android the SMS link is an App Link and opens the app directly, but iOS has no Universal
   * Link for this host, so a Leadman tapping the same SMS lands in Safari — on a page reached from
   * Messages, where Safari's own Back is disabled and the only exit is the small "Messages" chip in
   * the status bar. The app already accepts denali://gallery/{token} (see DeepLinkService), so this
   * hands them straight back to it, on the same photo when the link carried one.
   *
   * Shown ONLY to internal viewers: a client has no app to open, and the link would dead-end.
   */
  /** #CM24: shown when the deep link was tapped and nothing handled it. */
  appOpenFailed = false;

  /**
   * Feedback #CM24 — open the Leadman app, and say something useful when we can't.
   *
   * A `denali://` custom scheme is silent on failure: with the app not installed the browser
   * simply does nothing, so the button read as broken (reported after an uninstall). There is no
   * API that answers "is this app installed", so use the standard signal — if the page is STILL
   * visible a moment after we hand off, nothing took the link.
   *
   * Deliberately not a redirect to a store page: the person is already looking at the gallery they
   * wanted, so telling them they can carry on here is more useful than sending them away.
   */
  openInApp(): void {
    this.appOpenFailed = false;
    const returnedToUs = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.appOpenFailed = true;
        this.cdr.markForCheck();
      }
    };
    try {
      window.location.href = this.appDeepLink;
    } catch {
      // Some browsers throw outright on an unknown scheme rather than failing silently.
      this.appOpenFailed = true;
      return;
    }
    setTimeout(returnedToUs, 1500);
  }

  get appDeepLink(): string {
    const photo = this.deepPhotoId ? `?p=${encodeURIComponent(this.deepPhotoId)}` : '';
    return `denali://gallery/${encodeURIComponent(this.token)}${photo}`;
  }

  imageUrl(photo: GalleryPhoto): string { return this.galleryService.imageUrl(this.token, photo.galleryPhotoId); }

  get activePhoto(): GalleryPhoto | null { return this.allPhotos[this.activeIndex] || null; }

  // End of Day photos carry a placeholder sub-category ("NA" / "N/A") that means nothing to a
  // client, so blank it and let the template hide the chip rather than show an empty-looking tag.
  // Feedback #11g did this in the app; this page — the one the client actually sees — was missed.
  get activePhotoCategory(): string {
    const c = ((this.activePhoto && this.activePhoto.category) || '').trim();
    return /^n\/?a$/i.test(c) ? '' : c;
  }

  // Distinct people on a photo: the uploader plus anyone who commented.
  private facesFor(p: GalleryPhoto): string[] {
    const names: string[] = [];
    if (p && p.uploadedByName) { names.push(p.uploadedByName); }
    ((p && p.commentAuthors) || []).forEach((n: string) => {
      if (n && names.indexOf(n) === -1) { names.push(n); }
    });
    return names;
  }
  photoFaces(p: GalleryPhoto): string[] { return this.facesFor(p).slice(0, 3); }
  photoFacesOverflow(p: GalleryPhoto): number { return Math.max(0, this.facesFor(p).length - 3); }

  openPhoto(photo: GalleryPhoto) {
    const idx = this.allPhotos.findIndex(p => p.galleryPhotoId === photo.galleryPhotoId);
    this.activeIndex = idx < 0 ? 0 : idx;
    this.lightboxOpen = true;
    document.body.classList.add(SecurePhotoGalleryComponent.LIGHTBOX_CLASS);
    this.loadComments();
  }
  closeLightbox() {
    this.lightboxOpen = false;
    document.body.classList.remove(SecurePhotoGalleryComponent.LIGHTBOX_CLASS);
  }
  prev() { if (this.activeIndex > 0) { this.activeIndex--; this.loadComments(); } }
  next() { if (this.activeIndex < this.allPhotos.length - 1) { this.activeIndex++; this.loadComments(); } }

  loadComments() {
    const photo = this.activePhoto;
    if (!photo) return;
    this.comments = [];
    this.loadingComments = true;
    this.galleryService.getPhotoComments(this.token, photo.galleryPhotoId).subscribe({
      next: (res) => { this.loadingComments = false; this.comments = res?.comments || []; this.cdr.detectChanges(); },
      error: () => { this.loadingComments = false; this.cdr.detectChanges(); }
    });
  }

  postComment(el: HTMLTextAreaElement) {
    const photo = this.activePhoto;
    const text = (el?.value || '').trim();
    if (!photo || !text) return;
    this.posting = true;
    this.galleryService.addComment(this.token, {
      galleryPhotoId: photo.galleryPhotoId, contactRef: this.contactRef, commentText: text
    }).subscribe({
      next: (res: ApiResponse) => {
        this.posting = false;
        if (res.success) { el.value = ''; photo.commentCount = (photo.commentCount || 0) + 1; this.loadComments(); }
        else { this.toast.showWarning('Photo Gallery', res.message); }
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.posting = false;
        if (error?.status !== 401) { this.toast.showError('Photo Gallery', 'Unable to post comment. Please try again.'); }
        this.cdr.detectChanges();
      }
    });
  }

  onTouchStart(e: TouchEvent) { this.touchStartX = e.changedTouches[0].clientX; }
  onTouchEnd(e: TouchEvent) {
    const dx = e.changedTouches[0].clientX - this.touchStartX;
    if (Math.abs(dx) > 50) { dx > 0 ? this.prev() : this.next(); }
  }
}
